import React, { useState, useEffect, useCallback } from 'react';

const AmazighAdminTab = ({ API, isDarkMode, showNotification, adminToken }) => {
  const [adminDialect, setAdminDialect] = React.useState('tachelhit');
  const [settings, setSettings] = React.useState(null);
  const [translations, setTranslations] = React.useState([]);
  const [total, setTotal] = React.useState(0);
  const [page, setPage] = React.useState(1);
  const [search, setSearch] = React.useState('');
  const [searchInput, setSearchInput] = React.useState('');
  const [stats, setStats] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [showModal, setShowModal] = React.useState(false);
  const [editItem, setEditItem] = React.useState(null);
  const [formData, setFormData] = React.useState({ source_text: '', translated_text: '', latin_text: '', audio_url: '' });
  const [savingSettings, setSavingSettings] = React.useState(false);
  const [deleteConfirm, setDeleteConfirm] = React.useState(null);
  const [duplicateCheck, setDuplicateCheck] = React.useState(null);

  // Dynamic API prefix based on active dialect
  const apiPrefix = adminDialect === 'tachelhit' ? '/admin/amazigh' : '/admin/amazigh-central';

  const headers = { 'Authorization': `Bearer ${adminToken}`, 'Content-Type': 'application/json' };

  const loadSettings = React.useCallback(async () => {
    try {
      const res = await fetch(`${API}${apiPrefix}/settings`, { headers });
      if (res.ok) setSettings(await res.json());
    } catch (e) { console.error(e); }
  }, [API, adminToken, apiPrefix]);

  const loadTranslations = React.useCallback(async () => {
    try {
      const res = await fetch(`${API}${apiPrefix}/translations?page=${page}&search=${encodeURIComponent(search)}`, { headers });
      if (res.ok) {
        const data = await res.json();
        setTranslations(data.translations || []);
        setTotal(data.total || 0);
      }
    } catch (e) { console.error(e); }
  }, [API, adminToken, apiPrefix, page, search]);

  const loadStats = React.useCallback(async () => {
    try {
      const res = await fetch(`${API}${apiPrefix}/stats`, { headers });
      if (res.ok) setStats(await res.json());
    } catch (e) { console.error(e); }
  }, [API, adminToken, apiPrefix]);

  React.useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([loadSettings(), loadTranslations(), loadStats()]);
      setLoading(false);
    };
    init();
  }, [loadSettings, loadTranslations, loadStats]);

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const res = await fetch(`${API}${apiPrefix}/settings`, {
        method: 'PUT', headers, body: JSON.stringify(settings)
      });
      if (res.ok) showNotification("Settings saved!", "success");
      else showNotification("Failed to save settings", "error");
    } catch (e) { showNotification("Error saving settings", "error"); }
    setSavingSettings(false);
  };

  const doSaveTranslation = async () => {
    try {
      const itemId = editItem ? (editItem.id || editItem._id) : null;
      const url = itemId
        ? `${API}${apiPrefix}/translations/${itemId}`
        : `${API}${apiPrefix}/translations`;
      const method = itemId ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers, body: JSON.stringify(formData) });
      if (res.ok) {
        showNotification(editItem ? "Translation updated!" : "Translation added!", "success");
        setShowModal(false);
        setEditItem(null);
        setFormData({ source_text: '', translated_text: '', latin_text: '', audio_url: '' });
        setDuplicateCheck(null);
        loadTranslations();
        loadStats();
      } else {
        const err = await res.json().catch(() => ({}));
        showNotification(err.detail || "Failed to save", "error");
      }
    } catch (e) { showNotification("Error saving translation", "error"); }
  };

  const saveTranslation = async () => {
    if (!formData.source_text || !formData.translated_text) {
      showNotification("English and Tamazight text are required", "error");
      return;
    }
    // Skip duplicate check when editing an existing item
    if (editItem) {
      await doSaveTranslation();
      return;
    }
    // Check for duplicates before adding new
    try {
      const checkRes = await fetch(
        `${API}${apiPrefix}/translations/check-duplicate?source_text=${encodeURIComponent(formData.source_text)}`,
        { headers }
      );
      if (checkRes.ok) {
        const checkData = await checkRes.json();
        if (checkData.exists) {
          setDuplicateCheck(checkData.existing);
          return;
        }
      }
    } catch (e) { console.error("Duplicate check failed:", e); }
    await doSaveTranslation();
  };

  const deleteTranslation = async (id) => {
    if (!id) {
      showNotification("Cannot delete: missing translation ID. Try refreshing the page.", "error");
      return;
    }
    try {
      const res = await fetch(`${API}${apiPrefix}/translations/${id}`, { method: 'DELETE', headers });
      if (res.ok) {
        showNotification("Translation deleted", "success");
        loadTranslations();
        loadStats();
      } else {
        const err = await res.json().catch(() => ({}));
        showNotification(err.detail || "Failed to delete", "error");
      }
    } catch (e) { showNotification("Error deleting translation", "error"); }
    setDeleteConfirm(null);
  };

  const openAdd = () => {
    setEditItem(null);
    setFormData({ source_text: '', translated_text: '', latin_text: '', audio_url: '' });
    setShowModal(true);
  };

  const openEdit = (item) => {
    setEditItem(item);
    setFormData({
      source_text: item.source_text || '',
      translated_text: item.translated_text || '',
      latin_text: item.latin_text || '',
      audio_url: item.audio_url || ''
    });
    setShowModal(true);
  };

  if (loading) return (
    <div className="flex items-center justify-center py-12">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
    </div>
  );

  const totalPages = Math.ceil(total / 30);
  const cardClass = `p-6 rounded-lg border ${isDarkMode ? 'border-gray-700 bg-gray-700' : 'border-gray-200 bg-gray-50'}`;
  const inputClass = `w-full px-3 py-2 rounded-lg border ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300 text-gray-900'}`;
  const labelClass = `block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`;

  // Switch dialect handler
  const switchAdminDialect = (dialect) => {
    if (dialect === adminDialect) return;
    setAdminDialect(dialect);
    setPage(1);
    setSearch('');
    setSearchInput('');
    setTranslations([]);
    setStats(null);
    setSettings(null);
  };

  return (
    <div className="space-y-6">

      {/* Dialect Tabs */}
      <div className="flex gap-2">
        {[
          { key: 'tachelhit', label: 'Tachelhit (South)' },
          { key: 'central_atlas', label: 'Central Atlas (Middle)' }
        ].map(d => (
          <button
            key={d.key}
            onClick={() => switchAdminDialect(d.key)}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
              adminDialect === d.key
                ? 'bg-blue-600 text-white'
                : isDarkMode
                  ? 'bg-gray-700 text-gray-400 hover:bg-gray-600'
                  : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
            }`}
          >
            {d.label}
          </button>
        ))}
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-3 gap-4">
          <div className={cardClass}>
            <div className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{stats.total_translations}</div>
            <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Total Translations</div>
          </div>
          <div className={cardClass}>
            <div className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{stats.manual_translations}</div>
            <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Manual</div>
          </div>
          <div className={cardClass}>
            <div className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{stats.auto_translations}</div>
            <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Imported</div>
          </div>
        </div>
      )}

      {/* Settings */}
      {settings && (
        <div className={cardClass}>
          <h3 className={`text-lg font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Settings</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center gap-3">
              <label className={labelClass}>Page Visible</label>
              <button onClick={() => setSettings(s => ({...s, is_visible: !s.is_visible}))}
                className={`px-3 py-1 rounded-lg text-sm font-medium ${settings.is_visible ? 'bg-green-600 text-white' : isDarkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-300 text-gray-600'}`}>
                {settings.is_visible ? 'ON' : 'OFF'}
              </button>
            </div>
            <div className="flex items-center gap-3">
              <label className={labelClass}>Pro Only</label>
              <button onClick={() => setSettings(s => ({...s, is_pro_only: !s.is_pro_only}))}
                className={`px-3 py-1 rounded-lg text-sm font-medium ${settings.is_pro_only ? 'bg-purple-600 text-white' : isDarkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-300 text-gray-600'}`}>
                {settings.is_pro_only ? 'ON' : 'OFF'}
              </button>
            </div>
            <div>
              <label className={labelClass}>Max Character Limit</label>
              <input type="number" value={settings.max_input_chars || 200} min={10} max={2000}
                onChange={e => setSettings(s => ({...s, max_input_chars: parseInt(e.target.value) || 200}))}
                className={inputClass} />
            </div>
          </div>

          {/* Page Text Editor */}
          <h4 className={`text-md font-bold mt-6 mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Page Text Editor</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              ['page_title', 'Page Title'],
              ['page_subtitle', 'Subtitle'],
              ['beta_badge_text', 'Beta Badge Text'],
              ['translate_button_text', 'Translate Button'],
              ['copy_button_text', 'Copy Button'],
              ['clear_button_text', 'Clear Button'],
              ['input_placeholder', 'Input Placeholder'],
              ['output_placeholder', 'Output Placeholder'],
            ].map(([key, label]) => (
              <div key={key}>
                <label className={labelClass}>{label}</label>
                <input type="text" value={settings[key] || ''} onChange={e => setSettings(s => ({...s, [key]: e.target.value}))} className={inputClass} />
              </div>
            ))}
            <div className="md:col-span-2">
              <label className={labelClass}>Disclaimer Text</label>
              <textarea value={settings.disclaimer_text || ''} onChange={e => setSettings(s => ({...s, disclaimer_text: e.target.value}))}
                rows={3} className={inputClass} />
            </div>
          </div>

          <button onClick={saveSettings} disabled={savingSettings}
            className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium">
            {savingSettings ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      )}

      {/* Search Card */}
      <div className={cardClass}>
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <svg className={`w-5 h-5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input type="text" placeholder="Search translations by English or Tamazight..." value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { setSearch(searchInput); setPage(1); } }}
              className={`flex-1 px-4 py-2 rounded-lg border text-sm ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white placeholder-gray-400' : 'bg-white border-gray-300 placeholder-gray-400'}`}
              style={{ minWidth: '250px' }} />
            <button onClick={() => { setSearch(searchInput); setPage(1); }}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700">
              Search
            </button>
            {search && (
              <button onClick={() => { setSearchInput(''); setSearch(''); setPage(1); }}
                className={`px-3 py-1.5 rounded-lg text-sm ${isDarkMode ? 'bg-gray-600 text-gray-300 hover:bg-gray-500' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'}`}>
                Clear
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {total} result{total !== 1 ? 's' : ''}
            </span>
            <button onClick={openAdd}
              className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700">
              + Add Translation
            </button>
          </div>
        </div>
      </div>

      {/* Translations Table */}
      <div className={cardClass}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className={isDarkMode ? 'text-gray-400 border-b border-gray-600' : 'text-gray-500 border-b border-gray-200'}>
                <th className="text-left py-2 px-2">English</th>
                <th className="text-left py-2 px-2">Tifinagh</th>
                <th className="text-left py-2 px-2">Latin</th>
                <th className="text-left py-2 px-2">Audio</th>
                <th className="text-right py-2 px-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {translations.map(item => (
                <tr key={item.id || item._id || item.source_text} className={`${isDarkMode ? 'border-b border-gray-600 hover:bg-gray-600/50' : 'border-b border-gray-100 hover:bg-gray-100/50'} transition-colors`}>
                  <td className={`py-2 px-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>{item.source_text}</td>
                  <td className={`py-2 px-2 text-lg ${isDarkMode ? 'text-white' : 'text-gray-900'}`} style={{fontFamily: "'Noto Sans Tifinagh', sans-serif"}}>{item.translated_text}</td>
                  <td className={`py-2 px-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{item.latin_text || '—'}</td>
                  <td className="py-2 px-2">
                    {item.audio_url ? (
                      <button onClick={() => { const a = new Audio(item.audio_url); a.play().catch(() => {}); }}
                        className="text-blue-500 hover:text-blue-700" title="Play audio">
                        🔊
                      </button>
                    ) : (
                      <span className={isDarkMode ? 'text-gray-600' : 'text-gray-300'}>—</span>
                    )}
                  </td>
                  <td className="py-2 px-2 text-right whitespace-nowrap">
                    <button onClick={() => openEdit(item)} className="text-blue-500 hover:text-blue-700 mr-3 font-medium" title="Edit">Edit</button>
                    <button onClick={() => setDeleteConfirm(item)} className="text-red-500 hover:text-red-700 font-medium" title="Delete">Delete</button>
                  </td>
                </tr>
              ))}
              {translations.length === 0 && (
                <tr>
                  <td colSpan={5} className={`py-8 text-center ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    {search ? `No translations found for "${search}"` : 'No translations yet. Add one or translate from the user page.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-4 pt-4 border-t ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}">
            <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Showing {((page - 1) * 30) + 1}–{Math.min(page * 30, total)} of {total}
            </span>
            <div className="flex items-center gap-2">
              <button onClick={() => setPage(1)} disabled={page <= 1}
                className={`px-2 py-1 rounded text-sm ${isDarkMode ? 'bg-gray-600 text-gray-300 disabled:opacity-40' : 'bg-gray-200 text-gray-600 disabled:opacity-40'}`}>
                First
              </button>
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
                className={`px-3 py-1 rounded text-sm ${isDarkMode ? 'bg-gray-600 text-gray-300 disabled:opacity-40' : 'bg-gray-200 text-gray-600 disabled:opacity-40'}`}>
                Prev
              </button>
              <span className={`text-sm px-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                {page} / {totalPages}
              </span>
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
                className={`px-3 py-1 rounded text-sm ${isDarkMode ? 'bg-gray-600 text-gray-300 disabled:opacity-40' : 'bg-gray-200 text-gray-600 disabled:opacity-40'}`}>
                Next
              </button>
              <button onClick={() => setPage(totalPages)} disabled={page >= totalPages}
                className={`px-2 py-1 rounded text-sm ${isDarkMode ? 'bg-gray-600 text-gray-300 disabled:opacity-40' : 'bg-gray-200 text-gray-600 disabled:opacity-40'}`}>
                Last
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setDeleteConfirm(null)}>
          <div className={`w-full max-w-md rounded-xl p-6 ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-2xl`} onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </div>
              <div>
                <h3 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Delete Translation</h3>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>This action cannot be undone.</p>
              </div>
            </div>
            <div className={`p-3 rounded-lg mb-4 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
              <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                <span className="font-medium">English:</span> {deleteConfirm.source_text}
              </p>
              <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`} style={{fontFamily: "'Noto Sans Tifinagh', sans-serif"}}>
                <span className="font-medium" style={{fontFamily: 'inherit'}}>Tifinagh:</span> {deleteConfirm.translated_text}
              </p>
            </div>
            <div className="flex gap-3">
              <button onClick={() => deleteTranslation(deleteConfirm.id || deleteConfirm._id)}
                className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium transition-colors">
                Yes, Delete
              </button>
              <button onClick={() => setDeleteConfirm(null)}
                className={`flex-1 px-4 py-2.5 rounded-lg font-medium transition-colors ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Duplicate Detection Modal */}
      {duplicateCheck && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setDuplicateCheck(null)}>
          <div className={`w-full max-w-md rounded-xl p-6 ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-2xl`} onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div>
                <h3 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Word Already Exists</h3>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>"{formData.source_text}" is already in the database.</p>
              </div>
            </div>
            <div className={`p-3 rounded-lg mb-3 ${isDarkMode ? 'bg-yellow-900/30 border border-yellow-700' : 'bg-yellow-50 border border-yellow-300'}`}>
              <p className={`text-xs font-semibold mb-1 ${isDarkMode ? 'text-yellow-400' : 'text-yellow-700'}`}>EXISTING ENTRY:</p>
              <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                <span className="font-medium">English:</span> {duplicateCheck.source_text}
              </p>
              <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`} style={{fontFamily: "'Noto Sans Tifinagh', sans-serif"}}>
                <span className="font-medium" style={{fontFamily: 'inherit'}}>Tifinagh:</span> {duplicateCheck.translated_text}
              </p>
              {duplicateCheck.latin_text && (
                <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <span className="font-medium">Latin:</span> {duplicateCheck.latin_text}
                </p>
              )}
            </div>
            <div className={`p-3 rounded-lg mb-4 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
              <p className={`text-xs font-semibold mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>YOUR NEW ENTRY:</p>
              <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                <span className="font-medium">English:</span> {formData.source_text}
              </p>
              <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`} style={{fontFamily: "'Noto Sans Tifinagh', sans-serif"}}>
                <span className="font-medium" style={{fontFamily: 'inherit'}}>Tifinagh:</span> {formData.translated_text}
              </p>
              {formData.latin_text && (
                <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <span className="font-medium">Latin:</span> {formData.latin_text}
                </p>
              )}
            </div>
            <div className="flex gap-3">
              <button onClick={() => { setDuplicateCheck(null); doSaveTranslation(); }}
                className="flex-1 px-4 py-2.5 bg-orange-600 text-white rounded-lg hover:bg-orange-700 font-medium transition-colors">
                Override Existing
              </button>
              <button onClick={() => setDuplicateCheck(null)}
                className={`flex-1 px-4 py-2.5 rounded-lg font-medium transition-colors ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowModal(false)}>
          <div className={`w-full max-w-lg rounded-xl p-6 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`} onClick={e => e.stopPropagation()}>
            <h3 className={`text-lg font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              {editItem ? 'Edit Translation' : 'Add Translation'}
            </h3>
            <div className="space-y-4">
              <div>
                <label className={labelClass}>English Text</label>
                <input type="text" value={formData.source_text} onChange={e => setFormData(f => ({...f, source_text: e.target.value}))}
                  className={inputClass} placeholder="hello" />
              </div>
              <div>
                <label className={labelClass}>Tamazight (Tifinagh)</label>
                <input type="text" value={formData.translated_text} onChange={e => setFormData(f => ({...f, translated_text: e.target.value}))}
                  className={inputClass} placeholder="ⴰⵣⵓⵍ" style={{fontFamily: "'Noto Sans Tifinagh', sans-serif", fontSize: '1.2rem'}} />
              </div>
              <div>
                <label className={labelClass}>Latin Script</label>
                <input type="text" value={formData.latin_text} onChange={e => setFormData(f => ({...f, latin_text: e.target.value}))}
                  className={inputClass} placeholder="Azul" />
              </div>
              <div>
                <label className={labelClass}>Audio URL (Dropbox, optional)</label>
                <input type="text" value={formData.audio_url} onChange={e => setFormData(f => ({...f, audio_url: e.target.value}))}
                  className={inputClass} placeholder="https://www.dropbox.com/..." />
                {formData.audio_url && (
                  <button onClick={() => { const a = new Audio(formData.audio_url.replace('?dl=0', '?raw=1')); a.play().catch(() => showNotification("Can't play audio", "warning")); }}
                    className="mt-1 text-xs text-blue-500 hover:text-blue-700">
                    Preview Audio
                  </button>
                )}
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={saveTranslation}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium">
                {editItem ? 'Update' : 'Add'}
              </button>
              <button onClick={() => setShowModal(false)}
                className={`flex-1 px-4 py-2 rounded-lg font-medium ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default AmazighAdminTab;
